// Layer style interfaces

import { ILayerAttribExpression } from "./LayerAttribExpressionInterfaces";


export type TLayerStyleAttribType = 'number' | 'string';
export type TLayerStyleShapeType = 'point' | 'line' | 'polygon';

export const LAYER_STYLE_RULE_COLOR_SCHEMES = [ 'none', 'single color', 'unique values', 'classified', 'gradient' ] as const;
export type TLayerStyleRuleColorScheme = typeof LAYER_STYLE_RULE_COLOR_SCHEMES[number];

export const LAYER_STYLE_POINT_TYPES = [ 'none', 'circle', 'square', 'triangle', 'star', 'cross', 'x', 'diamond' ] as const;
export type TLayerStylePointType = typeof LAYER_STYLE_POINT_TYPES[number];

export const LAYER_STYLE_LINE_TYPES = [ 'none', 'solid', 'dashed', 'dotted' ] as const;
export type TLayerStyleLineType = typeof LAYER_STYLE_LINE_TYPES[number];


export interface ILayerStyleData
{
  version: number;  // 1=not used | 2=this new/current style
  layer_shape_type: TLayerStyleShapeType;
  active_style_id?: number;
  styles: ILayerStyle[];
}

export interface ILayerStyle
{
  id: number;
  name: string;
  description: string;
  default_rule: ILayerStyleRule;
  rules: ILayerStyleRule[];
  show_advanced_mode: boolean;
  active_rule_id?: number; // controls which rule is open (only one rule can be open at a time)
}

export interface ILayerStyleRule
{
  id: number;
  enabled: boolean;
  description: string;

  filter?: ILayerAttribExpression;

  // Zoom scale - controls at what zoom levels this rule applies
  min_scale?: number;               // 1-20 values used by the UI sliders | undefined means "inherit from default rule"
  min_scale_denominator?: number;  // scaled value used directly in the <MinScaleDenominator> tag in GS (for the geometry)
  max_scale?: number;               // 1-20 values used by the UI sliders | undefined means "inherit from default rule"
  max_scale_denominator?: number;  // scaled value used directly in the <MaxScaleDenominator> tag in GS (for the geometry)

  // At least one of these 2 must be defined and filled in for a Rule to be valid
  geometry?: ILayerStyleRule_Geometry;    // undefined means this section is disabled
  text_label?: ILayerStyleRule_TextLabel; // undefined means this section is disabled
}

export const LAYER_STYLE_POLY_FILL_PATTERNS = [ 'None', 'No Pattern', 'Slash', 'Backslash', 'Horizontal Line', 'Vertical Line', 'Square', 'Diamond', 'Circle' ] as const;
export type TLayerStylePolyFillPattern = typeof LAYER_STYLE_POLY_FILL_PATTERNS[number];

export interface ILayerStyleRule_Geometry
{
  color_scheme?: TLayerStyleRuleColorScheme;

  attribute_id?: number;  // Not used for 'single color' color schemes
  attribute_type?: TLayerStyleAttribType;  // Not used for 'single color' color schemes

  // Exactly ONE of these are used at a time, based on the style's 'color_scheme'
  single_color?: ILayerStyleType_SingleColor; // only used when mode='single color'
  unique_values?: ILayerStyleType_UniqueValues; // only used when mode='unique values'
  classified?: ILayerStyleType_Classified; // only used when mode='classified'
  gradient?: ILayerStyleType_Gradient; // only used when mode='gradient'

  // For POINT layers
  point_type?: TLayerStylePointType;
  point_size?: number;
  point_opacity?: number;  // applies to the main/inner circle (not the outline/halo)
  point_halo_size?: number;  // 0 means no halo
  point_halo_opacity?: number;  // 0 means no halo
  point_halo_color: string;

  // For LINE or POLYGON layers
  line_thickness?: number;  // 0 means no border at all
  line_opacity?: number;  // 0 means no border at all
  line_style?: TLayerStyleLineType; // solid, dashed, dotted (solid means no line style basically)
  line_halo_thickness?: number;    // 0 means no halo
  line_halo_color: string;
  line_halo_opacity?: number;    // 0 means no halo

  // For POLYGON layers
  poly_fill_opacity?: number; // 0 means no primary fill
  poly_fill_pattern?: TLayerStylePolyFillPattern;     // 'None' means no fill pattern
  poly_fill_pattern_color: string;
  poly_fill_pattern_spacing?: number;
  poly_fill_pattern_stroke_width?: number; // 0 means no stroke
  poly_fill_pattern_opacity?: number;   // 0 means no fill pattern
}

export const LAYER_STYLE_FONTS = [ 'None', 'Times', 'Arial' ] as const;
export type TLayerStyleFont = typeof LAYER_STYLE_FONTS[number];

export const LAYER_STYLE_TEXT_LABEL_SEPARATORS = [ 'None', 'Space', 'Dash', 'Round Brackets', 'Square Brackets', 'New Line' ] as const;
export type TLayerStyleTextLabelSeparator = typeof LAYER_STYLE_TEXT_LABEL_SEPARATORS[number];


export interface ILayerStyleRule_TextLabel
{
  primary_attribute_id?: number;
  secondary_attribute_id?: number;  // optional
  font_family?: TLayerStyleFont;
  size?: number;
  color: string;
  opacity?: number;
  bold?: boolean;
  halo_size?: number;   // 0 means there is no halo
  halo_color: string;
  halo_opacity?: number;   // 0 means there is no halo
  secondary_label_separator?: TLayerStyleTextLabelSeparator;
  word_wrap_width?: number;   // 0 to disable auto-wrap completely
  single_center_label?: boolean;  // for polygons, forces a single label in the center (no tile label duplicates)
}

export interface ILayerStyleType_SingleColor
{
  // For text and numerical attributes
  color: string;
}

export interface ILayerStyleType_UniqueValues
{
  // For text and numerical attributes
  items: ILayerStyleType_UniqueValuesItem[];
}

export interface ILayerStyleType_UniqueValuesItem
{
  // For text and numerical attributes
  id: number;
  value: string;
  color: string;
}

export interface ILayerStyleType_Classified
{
  // For numerical attributes only
  items: ILayerStyleType_ClassifiedItem[];
  preset_id?: number; // filled out if a gradient preset is currently selected
  auto_generated_values_interval?: number;   // if set, the current values were all auto-generated (no manual editing)
}

export interface ILayerStyleType_ClassifiedItem
{
  // For numerical attributes only
  id: number;
  value_min: number;
  value_max: number;
  color: string;
}

export interface ILayerStyleType_Gradient
{
  // For numerical attributes only
  id: number, 
  name?: string;  // optional name, must be unique
  preset_id?: number; // filled out if a preset is currently selected
  items: ILayerStyleType_GradientItem[];
}

export interface ILayerStyleType_GradientItem
{
  // For numerical attributes only
  id: number;
  value: number;
  color: string;
}
